import { useState } from "react";
import {
  BundleContentTypeEnum,
  ModelBundleSortFieldEnum,
  SortOrderEnum,
  useGetModelBundlesPaginatedListQuery,
} from "../../generated/graphql";
import {
  Container,
  InputAdornment,
  Table,
} from "@material-ui/core";
import { Search } from "@material-ui/icons";
import { BundleFormModal } from "./modals/BundleFormModal";
import { ErrorMessageSnackbar } from "../shared/ErrorMessageSnackbar";
import { SuccessSnackbar } from "../shared/SuccessSnackbar";
import { BundlesStorageHeader } from "./components/BundlesStorageHeader";
import { SearchTextField } from "../MobileAppModal/MobileAppModal.style";
import { useDebounce } from "../shared/Utils/OtherOperations/useDebounce";
import { BundlesTableHeader } from "./components/BundlesTableHeader";
import { BundlesTableList } from "./components/BundlesTableList";

const initialIsOpenModal = {
  isOpen: false,
  id: "",
};

export const initialNotifications = {
  error: false,
  success: false,
  message: "",
};

export type Filters = {
  contentTypes: BundleContentTypeEnum[];
  isDraft: boolean | null;
  tagIds: string[];
};

export const BundlesStorage = () => {
  const [isOpenModal, setIsOpenModal] = useState(initialIsOpenModal);
  const [notifications, setNotifications] = useState(initialNotifications);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState<Filters>({
    contentTypes: [],
    isDraft: null,
    tagIds: [],
  });
  const [sortField, setSortField] = useState<ModelBundleSortFieldEnum>(
    ModelBundleSortFieldEnum.CreatedDate,
  );
  const [sortOrder, setSortOrder] = useState<SortOrderEnum>(SortOrderEnum.Desc);
  const [searchKey, setSearchKey] = useState("");
  const debouncedSearchKey = useDebounce(searchKey.trim(), 800);

  const { data, loading } = useGetModelBundlesPaginatedListQuery({
    variables: {
      params: {
        limit: rowsPerPage,
        skip: page * rowsPerPage,
        search: debouncedSearchKey,
        filter: {
          contentTypes: filters.contentTypes.length ? filters.contentTypes : [],
          isDraft:
            typeof filters.isDraft === "boolean" ? filters.isDraft : undefined,
          tagIds: filters.tagIds.length ? filters.tagIds : [],
        },
        sort: {
          field: sortField || undefined,
          order: sortOrder || undefined,
        },
      },
    },
  });

  const models = data?.getModelBundlesPaginated?.items || [];
  const total = data?.getModelBundlesPaginated?.total || 0;

  return (
    <Container maxWidth="lg">
      <BundlesStorageHeader
        onOpenModal={() => setIsOpenModal({ isOpen: true, id: "" })}
      />
      <SearchTextField
        placeholder="Пошук за назвою моделі, номером або тегом"
        value={searchKey}
        inputProps={{ maxLength: 100 }}
        onChange={({ target }) => setSearchKey(target.value)}
        variant="outlined"
        size="small"
        style={{ width: "540px" }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
        }}
      />
      <Table>
        <BundlesTableHeader
          sortField={sortField}
          sortOrder={sortOrder}
          filters={filters}
          setSortField={setSortField}
          setSortOrder={setSortOrder}
          setFilters={setFilters}
          setPage={setPage}
        />
        <BundlesTableList 
          loading={loading}
          models={models}
          total={total}
          page={page}
          rowsPerPage={rowsPerPage}
          setIsOpenModal={setIsOpenModal}
          setPage={setPage}
          setRowsPerPage={setRowsPerPage}
        />
      </Table>
      {isOpenModal.isOpen && (
        <BundleFormModal
          id={isOpenModal.id}
          onClose={() => setIsOpenModal(initialIsOpenModal)}
          setNotifications={setNotifications}
        />
      )}
      {notifications?.error && (
        <ErrorMessageSnackbar
          open={notifications?.error}
          closeHandler={() => setNotifications(initialNotifications)}
          message={notifications?.message}
        />
      )}
      {notifications?.success && (
        <SuccessSnackbar
          open={notifications?.success}
          handleClose={() => setNotifications(initialNotifications)}
          message={notifications?.message}
          snackbarProps={{
            anchorOrigin: { vertical: "bottom", horizontal: "center" },
          }}
        />
      )}
    </Container>
  );
};
